import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Campana de notificaciones in-app. Portada de producción.
 * Se muestra solo para usuarios autenticados; consulta /api/client/notifications,
 * pinta un badge con las no vistas y permite marcarlas como leídas.
 */
export default function NotificationBell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const unseen = items.filter(n => !n.seen).length;

  const fetchItems = useCallback(() => {
    if (!user) return;
    fetch(`${BACKEND_URL}/api/client/notifications`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) { setItems([]); return; }
    fetchItems();
    const id = setInterval(fetchItems, 60000); // refresco cada 60s
    return () => clearInterval(id);
  }, [user, fetchItems]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unseen > 0) {
      // Marcar todas como vistas al abrir
      setItems(prev => prev.map(n => ({ ...n, seen: true })));
      try {
        await fetch(`${BACKEND_URL}/api/client/notifications/seen-all`, { method: 'POST', credentials: 'include' });
      } catch { /* noop */ }
    }
  };

  const onItemClick = (n) => {
    setOpen(false);
    if (n.booking_id) navigate('/client/dashboard');
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors text-gray-600"
        title={t('notifications.title', 'Notificaciones')}
        aria-label={t('notifications.title', 'Notificaciones')}
      >
        <Bell className="w-5 h-5" />
        {unseen > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unseen > 9 ? '9+' : unseen}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-bold text-gray-900 text-sm">{t('notifications.title', 'Notificaciones')}</span>
            {unseen > 0 && <span className="text-xs text-gray-400">{unseen} {t('notifications.new', 'nuevas')}</span>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-gray-400 text-sm flex flex-col items-center gap-2">
                <Check className="w-8 h-8 text-gray-200" />
                {t('notifications.empty', 'No tienes notificaciones')}
              </div>
            ) : (
              items.map(n => (
                <button
                  key={n.notification_id}
                  onClick={() => onItemClick(n)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${n.seen ? '' : 'bg-blue-50/40'}`}
                >
                  <p className="font-semibold text-sm text-gray-900">{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-snug">{n.message}</p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
